// Bloco 1b — gestão de usuários via /api/admin/users/*. Só verified_organizer
// acessa (backend gateado por policy `RequireVerifiedOrganizer`). Frontend
// adiciona AuthGuard requireRole="verified_organizer" como defesa em
// profundidade e pra esconder o link na UI.
//
// Magic link no MVP: backend devolve `magicLink` no response do invite.
// Frontend mostra com botões "Copiar" + "WhatsApp" — admin envia manualmente.

import { api } from './client';

export type Role =
  | 'organizer'
  | 'verified_organizer'
  | 'builder'
  | 'athlete'
  | 'support';

export const ALL_ROLES: Role[] = [
  'organizer',
  'verified_organizer',
  'builder',
  'athlete',
  'support',
];

export interface AdminUserSummary {
  uid: string;
  email: string;
  displayName: string | null;
  photoUrl: string | null;
  createdAtUtc: string | null;
  lastSignInAtUtc: string | null;
  roles: Role[];
  disabled: boolean;
}

export interface InviteUserRequest {
  email: string;
  name?: string;
  initialRoles?: Role[];
  continueUrl?: string;
}

export interface InviteUserResponse {
  uid: string;
  email: string;
  displayName: string | null;
  roles: Role[];
  magicLink: string;
}

export const adminUsersApi = {
  /// Busca user por email. Retorna `null` quando não existe (response body
  /// é `null` literal ou vazio — wrapper trata ambos).
  searchByEmail: async (email: string): Promise<AdminUserSummary | null> => {
    const result = await api<AdminUserSummary | null>(
      `/api/admin/users?email=${encodeURIComponent(email)}`,
    );
    return result ?? null;
  },

  /// Cria user + gera magic link. Idempotente: se email já existe, devolve
  /// user existente sem sobrescrever roles. continueUrl aponta pra rota
  /// `/auth/email-link` da SPA, que detecta o oobCode no URL e chama
  /// `signInWithEmailLink` pra completar a autenticação. Email vai no query
  /// pra que a página possa chamar o SDK sem pedir manualmente (Firebase
  /// exige o email no client por anti-phishing).
  invite: (body: InviteUserRequest): Promise<InviteUserResponse> => {
    const continueUrl =
      body.continueUrl ??
      `${window.location.origin}/auth/email-link?email=${encodeURIComponent(body.email)}`;
    return api<InviteUserResponse>('/api/admin/users/invite', {
      method: 'POST',
      body: { ...body, continueUrl },
    });
  },

  /// Substitui as roles do user (não acumula). Endpoint pré-existente do
  /// Bloco 1a do plano, reusado aqui.
  setRoles: (uid: string, roles: Role[]): Promise<{ uid: string; roles: Role[] }> =>
    api(`/api/admin/users/${encodeURIComponent(uid)}/roles`, {
      method: 'POST',
      body: { roles },
    }),

  /// Soft-delete: marca user como disabled no Firebase. Preserva FK no
  /// Postgres (events/registrations apontam pra ele). Reversível via
  /// Firebase Console (não exposto aqui).
  disable: (uid: string): Promise<void> =>
    api(`/api/admin/users/${encodeURIComponent(uid)}`, { method: 'DELETE' }),
};
